/**
 * Transforms widget tool responses into WidgetParts.
 *
 * When the AI calls the widget tool, we transform the tool response
 * into a WidgetPart that can be rendered by the client. This includes
 * denormalizing the widget data by resolving references from the injected
 * ToolOutputTrackerProcessor.
 *
 * Resolution priority:
 * 1. Direct `data` - highest priority (already has the data)
 * 2. `dataRef` - resolve by reference ID (lower priority)
 *
 * **Legacy-only note (ELARA-629):** the `dataRef` resolution path is kept
 * here purely for backwards compatibility with already-persisted historical
 * widget tool calls in Firestore. New widget tool invocations cannot pass
 * `dataRef` at all — the widget tool factory in `server/src/tools/widget-tool.ts`
 * registers tools with `inputSchema = schema.omit({ dataRef: true })`, so
 * the field is invisible to the LLM. Do not extend or rely on this dataRef
 * branch for new functionality; the `data` direct-pass path is the supported
 * one going forward.
 *
 * Used in pipelines:
 * - model-to-client: Transform widget tool to WidgetPart for client rendering
 */

import type { CoreLogger } from '@elara/core/logger';

import type { MessagePart, WidgetPart } from '#schemas/message-schemas.js';
import type { ProcessorContext } from '../types.js';
import type { PartResult } from './PartProcessor.js';

import { assert } from '@elara/core/errors';

import { isToolRequestPart, isToolResponsePart } from '#schemas/message-schemas.js';
import { isWidgetToolPayload, normalizeWidgetName, PreferenceProposalStatusSchema, WidgetSchemas } from '#schemas/widget-schemas.js';

import { PartProcessor } from './PartProcessor.js';
import { ToolOutputTrackerProcessor } from './ToolOutputTrackerProcessor.js';

function isFailureEnvelope(value: unknown): value is { success: false; error: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<string, unknown>).success === false &&
    typeof (value as Record<string, unknown>).error === 'string'
  );
}

function inferSymbolicDataRefFromToolCallRef(dataRef: string): string | undefined {
  const match = /^tool_([^_]+)_.+$/u.exec(dataRef.trim());
  if (!match) {
    return;
  }

  const toolName = match[1]?.trim();
  if (!toolName) {
    return;
  }

  return ToolOutputTrackerProcessor.toSymbolicDataRef(toolName);
}

export class WidgetTransformProcessor extends PartProcessor {
  readonly name = 'WidgetTransform';
  readonly #tracker: ToolOutputTrackerProcessor;

  constructor(tracker: ToolOutputTrackerProcessor, baseLogger: CoreLogger) {
    super(baseLogger);
    this.#tracker = tracker;
  }

  process(part: MessagePart, context: ProcessorContext): PartResult {
    if (!(isToolResponsePart(part) || isToolRequestPart(part))) return { parts: [part] };
    if (isToolRequestPart(part)) {
      // if part is a tool request for a widget, get rid of it, otherwise, pass through
      if (isWidgetToolPayload(part.toolRequest)) {
        return { parts: [] };
      } else {
        return { parts: [part] };
      }
    }
    const { toolResponse } = part;
    assert(toolResponse, '[Widgets] Tool response is required');
    if (!isWidgetToolPayload(toolResponse)) {
      return { parts: [part] };
    }
    // Normalize legacy widget names for backwards compatibility with existing chat history
    const widgetType = normalizeWidgetName(toolResponse.name);
    assert(widgetType, `[Widgets] Unknown widget type: ${toolResponse.name}`);
    const widgetSchema = WidgetSchemas[widgetType];
    assert(widgetSchema, `[Widgets] Widget schema not found for: ${widgetType}`);

    if (isFailureEnvelope(toolResponse.output)) {
      context.logger.warn(
        { widgetType, toolResponse },
        `[Widgets] ${widgetType} already has failure envelope; preserving original error`,
      );
      return { parts: [part], stopPropagation: true };
    }

    if (
      widgetType === 'showPreferenceProposalWidget' &&
      typeof toolResponse.output === 'object' && toolResponse.output !== null &&
      'status' in toolResponse.output &&
      PreferenceProposalStatusSchema.safeParse(toolResponse.output.status).success
    ) {
      return { parts: [part], stopPropagation: true };
    }

    context.logger.debug({ toolResponse }, `[Widgets] Processing ${widgetType} response`);

    const validationResult = widgetSchema.safeParse(toolResponse.output);
    if (!validationResult.success) {
      const error = `Invalid ${widgetType} widget data: ${validationResult.error.message}. Make sure that you're providing the data in the correct format for the ${widgetType} widget, and try again`;
      context.logger.error({ toolResponse, error: validationResult.error }, `[Widgets] ${widgetType} error: ${error}`);
      return {
        parts: [{ toolResponse: { ...toolResponse, output: { success: false, error } } }],
        stopPropagation: true,
      };
    }
    const widgetPayload = validationResult.data;
    context.logger.debug({ output: widgetPayload, widgetType }, `[Widgets] Parsed ${widgetType} output`);

    // Resolution priority: data > dataRef
    let widgetInput = widgetPayload.data;

    // Fall back to dataRef
    if (!widgetInput && widgetPayload.dataRef) {
      widgetInput = this.#tracker.getByRef(widgetPayload.dataRef) as typeof widgetInput;

      if (!widgetInput) {
        const symbolicRef = inferSymbolicDataRefFromToolCallRef(widgetPayload.dataRef);
        if (symbolicRef) {
          const symbolicResolved = this.#tracker.getByRef(symbolicRef) as typeof widgetInput;
          if (symbolicResolved) {
            widgetInput = symbolicResolved;
            context.logger.warn(
              { dataRef: widgetPayload.dataRef, symbolicRef, widgetType },
              `[Widgets] ${widgetType} resolved unresolved dataRef via symbolic fallback`,
            );
          }
        }
      }

      if (!widgetInput) {
        const error = `Could not resolve dataRef '${widgetPayload.dataRef}' provided to the ${widgetType} widget. Make sure that you're providing the correct 'dataRef' or that the widget is not expecting raw data as JSON in 'data' field, and try again`;
        context.logger.error(
          { dataRef: widgetPayload.dataRef, availableRefs: [...this.#tracker.toolOutputs.keys()] },
          `[Widgets] ${widgetType} error: ${error}`,
        );
        return {
          parts: [{ toolResponse: { ...toolResponse, output: { success: false, error } } }],
          stopPropagation: true,
        };
      }
      context.logger.debug(
        { widgetData: widgetInput, widgetType },
        `[Widgets] Resolved dataRef '${widgetPayload.dataRef}' to data for ${widgetType} widget`,
      );

      const dataSchema = widgetSchema.shape.data;
      const dataValidationResult = dataSchema.safeParse(widgetInput);
      if (!dataValidationResult.success) {
        const error = `dataRef '${widgetPayload.dataRef}' resolved to invalid data: ${dataValidationResult.error.message}. Make sure that you're providing the 'dataRef' from a compatible tool call output, and that the widget is not expecting a raw data as JSON in the 'data' field, and try again`;
        context.logger.error(
          { dataRef: widgetPayload.dataRef, error: dataValidationResult.error },
          `[Widgets] ${widgetType} error: ${error}`,
        );
        return {
          parts: [{ toolResponse: { ...toolResponse, output: { success: false, error } } }],
          stopPropagation: true,
        };
      }
      widgetInput = dataValidationResult.data;
    }

    // Filter out widgets without any data source (should never happen)
    if (widgetInput === undefined && !widgetPayload.dataRef) {
      const error = `Neither 'dataRef' nor 'data' was provided for the widget. Make sure that you're following the instructions for the ${widgetType} widget, and try again`;
      context.logger.error(
        { type: widgetType, toolResponse, widgetData: widgetInput },
        `[Widgets] ${widgetType} error: ${error}`,
      );
      return {
        parts: [{ toolResponse: { ...toolResponse, output: { success: false, error } } }],
        stopPropagation: true,
      };
    }

    const { marker } = toolResponse.output as { marker?: unknown };
    const widgetPart: WidgetPart = {
      widget: {
        type: widgetType,
        payload: {
          ...(widgetPayload.dataRef && { dataRef: widgetPayload.dataRef }),
          ...(widgetInput !== undefined && { data: widgetInput }),
          ...(typeof marker === 'string' && { marker }),
        },
      },
    };

    context.logger.debug(
      { widgetPart, originalToolResponse: toolResponse },
      `[Widgets] Transformed ${widgetType} to WidgetPart successfully`,
    );

    return { parts: [widgetPart], stopPropagation: true };
  }
}
